import {
  LitElement,
  html,
  css,
  classMap,
  styleMap,
  utils,
  disableBodyScroll,
  enableBodyScroll,
  ref,
  createRef,
} from '@hh/global.js'

class HHBottomSheet extends LitElement {
  static properties = {
    minimumSheetHeight: { type: String },
    sheetHeight: { type: String },
    dragPosition: {},
    dragInProgress: {
      type: Boolean,
      attribute: 'drag-in-progress',
      reflect: true,
    },
    isFullscreen: { type: Boolean, attribute: 'is-fullscreen', reflect: true },
  }

  $sheet = createRef()
  $content = createRef()

  constructor() {
    super()

    this.sheetHeight = '30vh'
    this.dragInProgress = false
    this.isFullscreen = false
    this.supportsDVH = CSS.supports('height', '100dvh')
    this.setHeightVariable()
  }

  connectedCallback() {
    super.connectedCallback()

    window.addEventListener('mousemove', this.onDragMove.bind(this))
    window.addEventListener('touchmove', this.onDragMove.bind(this), {
      passive: false,
    })

    window.addEventListener('mouseup', this.onDragEnd.bind(this))
    window.addEventListener('touchend', this.onDragEnd.bind(this))

    window.addEventListener('resize', this.onResize.bind(this))

    // Allows touchmove events to work where necessary
    // scrollable variant options, for instance
    this.querySelectorAll('[allow-touch-move]').forEach((node) => {
      node.addEventListener('touchmove', this.onInternalTouchMove.bind(this))
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('mousemove', this.onDragMove.bind(this))
    window.removeEventListener('touchmove', this.onDragMove.bind(this), {
      passive: false,
    })

    window.removeEventListener('mouseup', this.onDragEnd.bind(this))
    window.removeEventListener('touchend', this.onDragEnd.bind(this))

    window.removeEventListener('resize', this.onResize.bind(this))

    this.querySelectorAll('[allow-touch-move]').forEach((node) => {
      node.removeEventListener('touchmove', this.onInternalTouchMove.bind(this))
    })
  }

  onInternalTouchMove(event) {
    event.stopPropagation()
  }

  onResize() {
    this.setHeightVariable()
  }

  setHeightVariable() {
    const doc = document.documentElement
    let propertyValue = `${window.innerHeight}px`

    if (this.supportsDVH) {
      propertyValue = '100dvh'
    }

    doc.style.setProperty(`--hh-bottom-sheet-window-height`, propertyValue)
  }

  forceSheetHeight(value, setMinimumHeight) {
    const bottomSheetPaddingTop = this.getSheetPadding()
    const forcedHeight = value + bottomSheetPaddingTop + 'px'

    this.setSheetHeight(forcedHeight)

    if (setMinimumHeight) this.setMinimumHeight(value)
  }

  setMinimumHeight(value) {
    const bottomSheetPaddingTop = this.getSheetPadding()

    this.minimumSheetHeight = value + bottomSheetPaddingTop + 'px'
    this.style.setProperty(
      '--hh-bottom-sheet-min-height',
      this.minimumSheetHeight
    )

    document.body.style.setProperty(
      'padding-bottom',
      'var(--hh-bottom-sheet-min-height)'
    )
  }

  getSheetPadding() {
    return this.$sheet.value
      ? parseInt(
          window
            .getComputedStyle(this.$sheet.value)
            .getPropertyValue('padding-top'),
          10
        )
      : 0
  }

  setSheetHeight(value) {
    this.sheetHeight = value
  }

  touchPosition(event) {
    return event.touches ? event.touches[0] : event
  }

  onDragStart(event) {
    this.startPosition = this.dragPosition = this.touchPosition(event).pageY

    this.fullscreenOnStart = this.isFullscreen
  }

  onDragMove(event) {
    if (this.dragPosition === undefined) return
    if (event.composedPath().includes(this) && !this.fullscreenOnStart) {
      event.preventDefault()
    }

    this.dragInProgress = true
    const y = this.touchPosition(event).pageY
    const deltaY = this.dragPosition - y
    this.dragPosition = y

    this.setSheetHeight(
      `${this.$sheet.value?.getBoundingClientRect().height + deltaY}px`
    )
  }

  onDragEnd() {
    this.endPosition = this.dragPosition
    this.fullscreenOnStart = undefined

    if (
      this.dragPosition === undefined ||
      Math.abs(this.startPosition - this.endPosition) < 20
    ) {
      this.startPosition = undefined
      this.endPosition = undefined
      this.dragPosition = undefined

      return
    }

    this.dragPosition = undefined
    this.dragInProgress = false
    if (this.startPosition > this.endPosition) {
      this.setSheetHeight(window.innerHeight + 'px')
    } else {
      this.setSheetHeight(this.minimumSheetHeight || '0')
      this.$content.value?.scrollTo(0, 0)
    }
  }

  render() {
    this.isFullscreen = parseInt(this.sheetHeight, 10) >= window.innerHeight

    document.body.style.setProperty(
      'overflow',
      this.isFullscreen ? 'hidden' : ''
    )

    const classes = {
      contents: true,
      column: true,
      fullscreen: this.isFullscreen,
      'not-selectable': this.dragInProgress,
    }

    if (this.isFullscreen) {
      // https://github.com/willmcpo/body-scroll-lock/issues/237#issuecomment-1062527105
      // Fixes issue with scroll position reset on iOS.
      // Disabling scroll works with temporary overwrite
      const storedRequestAnimationFrame = window.requestAnimationFrame

      window.requestAnimationFrame = () => 42
      this.$content.value && disableBodyScroll(this.$content.value)
      window.requestAnimationFrame = storedRequestAnimationFrame
    } else {
      // Enabling scroll stays the same
      this.$content.value && enableBodyScroll(this.$content.value)
    }

    if (!utils.isTablet()) return html`<div><slot></slot></div>`

    return html`<div
      id="sheet"
      class=${classMap(classes)}
      style=${styleMap({
        height: this.isFullscreen
          ? 'var(--hh-bottom-sheet-window-height)'
          : this.sheetHeight,
      })}
      ${ref(this.$sheet)}
      @touchstart=${this.onDragStart}
    >
      <header class="controls">
        <div class="draggable-area" @mousedown=${this.onDragStart}>
          <div class="draggable-thumb"></div>
        </div>
      </header>

      <main class="body fill column" ${ref(this.$content)}>
        <slot></slot>
      </main>
    </div>`
  }

  static styles = css`
    :host {
      box-sizing: border-box !important;
    }

    @media screen and (max-width: 989px) {
      :host {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: var(--hh-bottom-sheet-z-index, 9);
      }

      :host([is-fullscreen]),
      :host([drag-in-progress]) {
        z-index: var(--hh-bottom-sheet-fullscreen-z-index, 9999);
      }

      .contents {
        position: relative;
        background: var(--hh-bottom-sheet-background, #fff);
        border: var(--hh-bottom-sheet-border, solid #000);
        border-width: var(--hh-bottom-sheet-border-width, 1px 0 0 0);
        border-radius: var(--hh-bottom-sheet-border-radius, 0);
        will-change: height;
        transition: border-radius var(--hh-bottom-sheet-duration, 500ms);
        max-height: var(--hh-bottom-sheet-window-height, 100vh);
        height: 22vh;
        min-height: var(--hh-bottom-sheet-min-height, 13.5rem);
        max-width: var(--hh-bottom-sheet-max-width, 100%);
        margin: var(--hh-bottom-sheet-margin, 0 auto);
        box-sizing: border-box;
        padding: var(--hh-bottom-sheet-padding, 3rem 0 0);
      }

      .contents:not(.not-selectable) {
        transition: border-radius var(--hh-bottom-sheet-duration, 500ms),
          height var(--hh-bottom-sheet-duration, 500ms) ease-out;
      }

      .contents.fullscreen {
        border-radius: 0;
      }

      .draggable-area {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        width: var(--hh-bottom-sheet-thumb-width, 4rem);
        margin: auto;
        padding: 1rem;
        cursor: grab;
      }

      .draggable-area::after {
        content: '';
        position: absolute;
        inset: -1rem;
      }

      .draggable-thumb {
        width: inherit;
        height: var(--hh-bottom-sheet-thumb-height, 0.1rem);
        background: var(--hh-bottom-sheet-thumb-background, #666);
        border-radius: 3rem;
      }

      :host(:not([is-fullscreen]):not([drag-in-progress])) .body {
        overflow-y: hidden;
      }

      .body {
        height: 100%;
        overflow-y: auto;
        overscroll-behavior: contain;
      }
    }
  `
}

customElements.define('hh-bottom-sheet', HHBottomSheet)
